import React from "react";

interface FormExtrasProps {
    deductions: {
        transportationToCanada: string;
        constructionCosts: string;
        exportPackaging: string;
    };
    additions: {
        transportationToDirectJourney: string;
        commissions: string;
        exportPacking: string;
    };
    other: {
        royaltyPayments: boolean;
        suppliedGoods: boolean;
    };
    onDeductionsChange: (field: string, value: string) => void;
    onAdditionsChange: (field: string, value: string) => void;
    onOtherChange: (field: string, value: boolean) => void;
}

export const FormExtras: React.FC<FormExtrasProps> = ({
    deductions,
    additions,
    other,
    onDeductionsChange,
    onAdditionsChange,
    onOtherChange,
}) => {
    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            {/* Deductions Section */}
            <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">
                    Deductions
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2 italic">
                        (if applicable)
                    </span>
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Identify any of these values that are included in your invoice total:
                </p>

                <div className="grid grid-cols-2">
                    <div className="space-y-4 pr-4">
                        <div>
                            <label
                                htmlFor="deductions.transportationToCanada"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Transportation charges for direct journey to Canada
                            </label>
                            <input
                                type="text"
                                id="deductions.transportationToCanada"
                                value={deductions.transportationToCanada}
                                onChange={(e) => onDeductionsChange('transportationToCanada', e.target.value)}
                                placeholder="Enter amount"
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="deductions.constructionCosts"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Costs for construction, erection, assembly done within Canada
                            </label>
                            <input
                                type="text"
                                id="deductions.constructionCosts"
                                value={deductions.constructionCosts}
                                onChange={(e) => onDeductionsChange('constructionCosts', e.target.value)}
                                placeholder="Enter amount"
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="deductions.exportPackaging"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Export Packaging
                            </label>
                            <input
                                type="text"
                                id="deductions.exportPackaging"
                                value={deductions.exportPackaging}
                                onChange={(e) => onDeductionsChange('exportPackaging', e.target.value)}
                                placeholder="Enter amount"
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            />
                        </div>
                    </div>
                    <div>{/* Empty column */}</div>
                </div>
            </div>

            {/* Additions Section */}
            <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">
                    Additions
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2 italic">
                        (if applicable)
                    </span>
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Identify any of these values that are not included in your invoice total:
                </p>

                <div className="grid grid-cols-2">
                    <div className="space-y-4 pr-4">
                        <div>
                            <label
                                htmlFor="additions.transportationToDirectJourney"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Transportation charges to the place of the direct journey to Canada
                            </label>
                            <input
                                type="text"
                                id="additions.transportationToDirectJourney"
                                value={additions.transportationToDirectJourney}
                                onChange={(e) => onAdditionsChange('transportationToDirectJourney', e.target.value)}
                                placeholder="Enter amount"
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="additions.commissions"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Amounts for commissions other than buying commissions
                            </label>
                            <input
                                type="text"
                                id="additions.commissions"
                                value={additions.commissions}
                                onChange={(e) => onAdditionsChange('commissions', e.target.value)}
                                placeholder="Enter amount"
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="additions.exportPacking"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Export Packing
                            </label>
                            <input
                                type="text"
                                id="additions.exportPacking"
                                value={additions.exportPacking}
                                onChange={(e) => onAdditionsChange('exportPacking', e.target.value)}
                                placeholder="Enter amount"
                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            />
                        </div>
                    </div>
                    <div>{/* Empty column */}</div>
                </div>
            </div>

            {/* Other Section */}
            <div>
                <h3 className="text-lg font-medium mb-3">
                    Other
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2 italic">
                        (check if applicable)
                    </span>
                </h3>

                <div className="space-y-4">
                    <div className="flex items-start">
                        <input
                            type="checkbox"
                            id="other.royaltyPayments"
                            checked={other.royaltyPayments}
                            onChange={(e) => onOtherChange('royaltyPayments', e.target.checked)}
                            className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700"
                        />
                        <label
                            htmlFor="other.royaltyPayments"
                            className="ml-3 text-sm text-gray-700 dark:text-gray-300"
                        >
                            Royalty payments or subsequent proceeds are paid or payable by the purchaser
                        </label>
                    </div>

                    <div className="flex items-start">
                        <input
                            type="checkbox"
                            id="other.suppliedGoods"
                            checked={other.suppliedGoods}
                            onChange={(e) => onOtherChange('suppliedGoods', e.target.checked)}
                            className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700"
                        />
                        <label
                            htmlFor="other.suppliedGoods"
                            className="ml-3 text-sm text-gray-700 dark:text-gray-300"
                        >
                            The purchaser has supplied goods or services for use in the production of these goods
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};
